// Servicio de localización simplificado - solo funciones utilitarias
class LocationService {
  constructor() {
    this.currentLocation = null;
  }

  // Verificar si la geolocalización está disponible
  isSupported() {
    return 'geolocation' in navigator;
  }

  // Verificar si estamos en contexto seguro (HTTPS)
  isSecureContext() {
    return window.isSecureContext || 
           window.location.protocol === 'https:' || 
           window.location.hostname === 'localhost' ||
           window.location.hostname === '127.0.0.1';
  }

  // Solicitar ubicación directamente (para usar en botón de contacto)
  async requestLocationForContact() {
    if (!this.isSupported()) {
      throw new Error('Geolocalización no soportada');
    }

    if (!this.isSecureContext()) {
      throw new Error('Se requiere HTTPS para usar geolocalización');
    }

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            
            // Obtener dirección
            const address = await this.getAddressFromCoordinates(latitude, longitude);
            
            const locationData = {
              coordinates: { lat: latitude, lng: longitude },
              address: address,
              mapsLink: this.generateGoogleMapsLink(latitude, longitude),
              accuracy: position.coords.accuracy
            };

            this.currentLocation = locationData;
            resolve(locationData);
          } catch (error) {
            reject(error);
          }
        },
        (error) => {
          reject(this.getErrorMessage(error));
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 60000
        }
      );
    });
  }

  // Obtener dirección desde coordenadas usando servicios gratuitos
  async getAddressFromCoordinates(latitude, longitude) {
    try {
      // Usar Nominatim (OpenStreetMap) - servicio gratuito y confiable
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1&accept-language=es&zoom=18`,
        {
          headers: {
            'User-Agent': 'MascotagApp/1.0'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error en el servicio de geocoding');
      }

      const data = await response.json();
      
      if (!data.address) {
        return 'Dirección no disponible';
      }

      const addr = data.address;
      let address = '';

      // Construir dirección con la información disponible
      if (addr.house_number && addr.road) {
        address = `${addr.road} ${addr.house_number}`;
      } else if (addr.road) {
        address = addr.road;
      } else if (addr.suburb) {
        address = addr.suburb;
      } else if (addr.city) {
        address = addr.city;
      }

      // Agregar ciudad si no está incluida
      if (addr.city && !address.includes(addr.city)) {
        address += `, ${addr.city}`;
      }

      return address.trim() || 'Dirección no disponible';
    } catch (error) {
      console.warn('Error obteniendo dirección:', error);
      return 'Dirección no disponible';
    }
  }

  // Generar enlace de Google Maps
  generateGoogleMapsLink(latitude, longitude) {
    return `https://www.google.com/maps?q=${latitude},${longitude}`;
  }

  // Obtener mensaje de error específico
  getErrorMessage(error) {
    switch (error.code) {
      case 1:
        return 'PERMISSION_DENIED';
      case 2:
        return 'POSITION_UNAVAILABLE';
      case 3:
        return 'TIMEOUT';
      default:
        return 'Error desconocido al obtener la ubicación';
    }
  }

  // Obtener guía específica para móviles
  getMobilePermissionGuide() {
    const userAgent = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
    const isAndroid = /Android/i.test(userAgent);

    if (isIOS) {
      return {
        title: 'Para habilitar ubicación en iOS',
        steps: [
          '1. Ve a Configuración > Privacidad y seguridad > Servicios de ubicación',
          '2. Asegúrate de que los Servicios de ubicación estén ACTIVADOS',
          '3. Desplázate hacia abajo y selecciona Safari',
          '4. Elige "Mientras se usa la app" o "Siempre"',
          '5. Regresa a la app y vuelve a intentar'
        ]
      };
    } else if (isAndroid) {
      return {
        title: 'Para habilitar ubicación en Android',
        steps: [
          '1. Ve a Configuración > Aplicaciones > [Tu navegador]',
          '2. Selecciona Permisos > Ubicación',
          '3. Elige "Permitir solo con la app en uso" o "Permitir siempre"',
          '4. También verifica: Configuración > Ubicación (debe estar ACTIVADA)',
          '5. Regresa a la app y vuelve a intentar'
        ]
      };
    } else {
      return {
        title: 'Para habilitar ubicación',
        steps: [
          '1. Haz clic en el ícono de candado en la barra de direcciones',
          '2. Selecciona "Permitir" para la ubicación',
          '3. Recarga la página y vuelve a intentar'
        ]
      };
    }
  }

  // Obtener ubicación actual almacenada
  getCurrentLocation() {
    return this.currentLocation;
  }

  // Limpiar datos
  clearLocation() {
    this.currentLocation = null;
  }
}

const locationService = new LocationService();
export default locationService;